import { useEffect, useCallback, useRef } from 'react';
import { App } from 'antd';

interface UseUnsavedChangesWarningOptions {
  hasUnsavedChanges: boolean;
  message?: string;
  title?: string;
}

/**
 * Warns the user about unsaved changes when:
 * - They try to refresh or close the browser tab (native browser dialog)
 * - They click an explicit back/cancel button (via the returned `confirmDiscard` helper)
 *
 * Note: in-app navigation via sidebar is NOT blocked because that requires
 * React Router's data router (createBrowserRouter), which this app does not use.
 */
export const useUnsavedChangesWarning = ({
  hasUnsavedChanges,
  message = 'Tienes productos agregados que no se han guardado. Si sales perderás los cambios.',
  title = '¿Salir sin guardar?',
}: UseUnsavedChangesWarningOptions) => {
  // Use the contextual modal from antd's <App> wrapper. The static
  // Modal.confirm() doesn't work when the app uses <App> for theme/context.
  const { modal } = App.useApp();

  // Defensive check: if there's no <App> ancestor, App.useApp() returns a no-op
  // modal and confirmDiscard would silently let the user lose changes.
  // Run inside an effect so it fires once per modal change instead of every render.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && !modal) {
      // eslint-disable-next-line no-console
      console.warn(
        '[useUnsavedChangesWarning] No antd <App> ancestor found. ' +
        'modal.confirm will be a no-op and the user will be able to discard changes silently.'
      );
    }
  }, [modal]);

  // Use a ref so the helper always sees the latest value without
  // needing to be recreated (which could break stale closures).
  const hasUnsavedRef = useRef(hasUnsavedChanges);
  useEffect(() => {
    hasUnsavedRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Warn on browser refresh / tab close
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  /**
   * Imperative helper: call with the action to run if the user confirms.
   * If there are no unsaved changes, the action runs immediately.
   */
  const confirmDiscard = useCallback((onConfirm: () => void) => {
    if (!hasUnsavedRef.current) {
      onConfirm();
      return;
    }
    modal.confirm({
      title,
      content: message,
      okText: 'Salir y descartar',
      cancelText: 'Continuar editando',
      okButtonProps: { danger: true },
      onOk: onConfirm,
    });
  }, [modal, title, message]);

  return { confirmDiscard };
};
